import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import FAQSchema from '@/components/schema/FAQSchema';

const FAQ = () => {
  const faqs = [
    {
      category: 'General Questions',
      questions: [
        {
          question: 'What is an Energy Performance Certificate (EPC)?',
          answer: 'An EPC is a document that shows the energy efficiency rating of a property. It rates a property from A (most efficient) to G (least efficient) and includes recommendations for improving energy efficiency. EPCs are required when selling or renting a property in the UK.'
        },
        {
          question: 'How long does an EPC last?',
          answer: 'An EPC is valid for 10 years from the date of issue. You can use the same EPC for multiple sales or tenancies within this period.'
        },
        {
          question: 'How long does an EPC assessment take?',
          answer: 'A typical domestic EPC assessment takes 30-60 minutes, depending on the size and complexity of the property. Commercial assessments may take longer.'
        },
        {
          question: 'When will I receive my certificate?',
          answer: 'We aim to deliver your certificate within 48 hours of the assessment. Digital services like SAP calculations typically take 3-5 working days.'
        }
      ]
    },
    {
      category: 'Pricing & Payment',
      questions: [
        {
          question: 'Do you charge VAT?',
          answer: 'No, we are not VAT registered, so no VAT is added to any of our prices. All prices shown on the website are the final amount you pay. For corporate clients: as we are not VAT registered, no VAT invoice is issued and no VAT can be reclaimed.'
        },
        {
          question: 'What is the travel surcharge?',
          answer: 'Properties within 1 hour of Exeter are included at standard pricing. For properties beyond 1 hour, we charge £25 per hour of travel time. Nationwide digital services have no travel surcharge.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept BACS bank transfer, PayPal, credit/debit cards, cash, and cheque. BACS payment details will be provided on your invoice.'
        },
        {
          question: 'Can I get a refund?',
          answer: 'Refunds are considered on a case-by-case basis. Please contact us to discuss your specific situation.'
        }
      ]
    },
    {
      category: 'Services',
      questions: [
        {
          question: 'What areas do you cover?',
          answer: 'We provide domestic EPCs in Exeter and surrounding areas, commercial EPCs across the South West, and nationwide digital services including SAP calculations, U-values, SBEM, thermal bridge calculations, dynamic thermal modelling, and retrofit coordination.'
        },
        {
          question: 'What is the difference between a domestic and commercial EPC?',
          answer: 'Domestic EPCs are for residential properties (houses, flats, etc.), while commercial EPCs are for non-residential buildings (offices, shops, warehouses, etc.). They use different assessment methodologies and have different requirements.'
        },
        {
          question: 'What is a SAP calculation?',
          answer: 'SAP (Standard Assessment Procedure) is a methodology used to assess and compare the energy and environmental performance of new dwellings. It\'s required for Building Regulations compliance on new builds and extensions.'
        },
        {
          question: 'What are U-value calculations?',
          answer: 'U-values measure how effective a building element (wall, floor, roof) is as an insulator. Lower U-values indicate better insulation. These calculations are required for Building Regulations compliance.'
        }
      ]
    },
    {
      category: 'Booking & Process',
      questions: [
        {
          question: 'How do I book an assessment?',
          answer: 'You can book online through our website using our simple booking system, or contact us by phone or email. We\'ll confirm your appointment and provide all necessary details.'
        },
        {
          question: 'What do I need to prepare for an assessment?',
          answer: 'Please ensure we have access to all areas of the property, including loft spaces and outbuildings. Have details of heating systems, insulation, and any recent improvements available. No need to tidy up - we\'re assessing the building, not your housekeeping!'
        },
        {
          question: 'Can I use your floor plan service?',
          answer: 'Yes! We offer professional floor plans for marketing and leasehold purposes. We also partner with HousePlanDirect.com where you can create your own plans from £30 using phone photos.'
        },
        {
          question: 'Do you offer retrofit coordination?',
          answer: 'Yes, we provide PAS 2035 retrofit coordination services nationwide. This is essential for energy efficiency projects requiring government funding or meeting modern standards. Contact us for a custom quote.'
        }
      ]
    },
    {
      category: 'Commercial EPC, NDEPC & Non-Domestic Services',
      questions: [
        {
          question: 'What is an NDEPC and how is it different from a residential EPC?',
          answer: 'NDEPC stands for Non-Domestic Energy Performance Certificate — the formal name for a Commercial EPC. It rates non-dwellings (offices, shops, warehouses, hotels, surgeries, etc.) on the same A–G scale but uses different software (SBEM/DSM rather than SAP/RdSAP) and is produced by an accredited Non-Domestic Energy Assessor (NDEA) rather than a Domestic Energy Assessor.'
        },
        {
          question: 'What is a Level 3 Commercial EPC?',
          answer: 'Level 3 is the most common non-domestic EPC category and covers simple buildings such as offices, shops, warehouses, light industrial units, pubs, cafés, B&Bs, surgeries and most SME commercial premises. Most properties we survey across Devon, Cornwall, Somerset and Dorset fall comfortably within Level 3 — see our /commercial-epc pillar page for the full process.'
        },
        {
          question: 'What is SBEM and is it the same as a Commercial EPC?',
          answer: 'SBEM (Simplified Building Energy Model) is the government-approved calculation engine used to produce a Level 3 Commercial EPC. It models the building\'s annual CO₂ emissions based on fabric U-values, heating, cooling, ventilation, hot water and lighting, and compares it against a notional building of the same geometry built to current Building Regulations. The output of an SBEM calculation is the NDEPC asset rating.'
        },
        {
          question: 'I am a commercial landlord with an F or G rated building — what should I do?',
          answer: 'Since 1 April 2023 it has been unlawful to let a commercial property rated F or G in England and Wales unless a valid exemption is registered on the PRS Exemptions Register. In DESNZ\'s June 2026 interim response, the previously proposed 2027 EPC C milestone was dropped; a proposed EPC B by 2031 will apply only to buildings over 1,000 m² (pending secondary legislation). Buildings under 1,000 m² remain at Band E with no further deadline currently defined. We pair the EPC with a separate MEES Commercial Advice Report that models realistic improvement options (LED re-lamping, controls, insulation, heat pump retrofit, solar PV) with cost and 7-year payback analysis so you have a defensible compliance plan.'
        },
        {
          question: 'How quickly can you do a Commercial EPC across the South West?',
          answer: 'For Devon, Somerset, Dorset and most of Cornwall we can attend site within 5–7 working days of booking. The on-site survey itself takes 1–3 hours depending on size and complexity. The lodged NDEPC is usually emailed within a further 5–7 working days. Urgent same-week service is available — contact us if your transaction or compliance deadline is tight.'
        },
        {
          question: 'Do you cover the whole of Devon, Cornwall, Somerset and Dorset?',
          answer: 'Yes. Our NDEA assessors are based in Exeter and we publish dedicated location pages for each county — /commercial-epc-devon, /commercial-epc-cornwall, /commercial-epc-somerset, /commercial-epc-dorset — plus city-level pages for Exeter, Plymouth, Torquay, Newton Abbot, Barnstaple, Taunton, Bridgwater, Yeovil, Truro, Falmouth, Newquay, Bournemouth, Poole and Dorchester. Travel surcharges (where applicable) are calculated transparently at booking.'
        }
      ]
    },
    {
      category: 'MEES Compliance for Landlords',
      questions: [
        {
          question: 'What is MEES and do I need to comply?',
          answer: 'MEES (Minimum Energy Efficiency Standards) — set out in the Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015 — requires privately rented properties in England and Wales to meet a minimum EPC rating. The current statutory minimum for both domestic and non-domestic private rented property is Band E (since 1 April 2023 for continuing tenancies, since 1 April 2018 for new tenancies). If you are letting a domestic or commercial property rated F or G, you must improve it to at least Band E or register a valid exemption on the PRS Exemptions Register. Future direction: domestic rises to Band C from 1 October 2030 (confirmed); non-domestic EPC B by 2031 is proposed for buildings over 1,000 m² only, pending secondary legislation.'
        },
        {
          question: 'What are the penalties for non-compliance with MEES?',
          answer: 'Penalties are set out in the Regulations and enforced by the Local Weights and Measures Authority. Non-domestic penalties are calculated by reference to the property\'s rateable value and the breach duration (see Regulation 39 of the Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015); the enforcement authority may also publish a notice of non-compliance on the PRS Exemptions Register. Domestic penalties are lower, with the government announcing an increase to a maximum of £30,000 per property under the incoming domestic MEES reforms. Landlords should refer to the DESNZ landlord guidance for current figures.'
        },
        {
          question: 'How can your Energy Advice Report help with MEES compliance?',
          answer: 'Our Domestic (DEAR) and Commercial (NDEAR) Energy Advice Reports across the South West UK include structured MEES compliance guidance. We identify your property\'s current rating, provide cost-effective improvement recommendations prioritised by payback period, and help identify whether any registered exemptions apply. For domestic properties we model the recommendations against the current £3,500 cost cap (rising to £10,000 per property from October 2030 under the confirmed domestic MEES reforms). For non-domestic properties we assess each measure against the 7-year payback test. This gives you a clear roadmap to compliance and the evidence pack needed to register an exemption if one applies.'
        },
        {
          question: 'What is the £3,500 cost cap for domestic MEES improvements?',
          answer: 'Under the current domestic MEES rules, a landlord is not required to spend more than £3,500 (including VAT) per property to raise the EPC to Band E. If measures costing £3,500 or less do not achieve Band E, the landlord may register a "high cost" exemption on the PRS Exemptions Register (valid 5 years). The cap will change under the confirmed 2030 domestic MEES reforms: the new Band C target from 1 October 2030 comes with a £10,000 cost cap per property (or 10% of property value for properties under £100,000), and the cost-cap exemption will be valid for 10 years. Our Energy Advice Report documents everything you need for exemption registration under either regime.'
        },
        {
          question: 'What MEES exemptions are available?',
          answer: 'Non-domestic exemptions (per the DESNZ landlord guidance) include: (1) 7-year payback exemption; (2) all improvements made exemption; (3) wall insulation exemption (where a recognised expert confirms cavity/external/internal wall insulation would harm the fabric or structure); (4) third-party consent exemption; (5) property devaluation exemption (RICS-registered valuer confirming >5% devaluation); (6) 6-month temporary exemption where a person recently became the landlord. Most non-domestic exemptions last 5 years. Domestic exemptions follow a similar structure with the domestic cost cap in place of the 7-year payback test. All exemptions must be registered on the PRS Exemptions Register before they can be relied on. Our service helps identify applicable exemptions and prepares the supporting evidence pack.'
        },
        {
          question: 'Does MEES apply to commercial properties?',
          answer: 'Yes. Non-domestic MEES applies to all privately rented non-domestic property in England and Wales that is legally required to have an EPC and is let on a "term certain" between 6 months and 99 years — offices, retail units, warehouses, industrial buildings, hotels, hospitality and mixed-use. Same Band E minimum as domestic (since April 2023) but the qualifying filter is the 7-year payback exemption (not the £3,500 cost cap). Our Commercial Energy Advice Report provides tailored MEES guidance for commercial landlords across the South West and is designed to be a defensible compliance file for tenants, lenders and insurers.'
        },
        {
          question: 'Which areas do you cover for MEES compliance advice?',
          answer: 'We provide MEES compliance advice throughout the South West UK including Devon, Cornwall, Somerset, Dorset, Bristol, Plymouth, Exeter, Bath, Bournemouth, and surrounding areas. Our Energy Advice Reports include site visits where required and detailed recommendations specific to regional property types and local improvement costs.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="FAQ - EPC & Energy Assessment Questions | Energy Performance Direct"
        description="Common questions about EPCs, SAP calculations, MEES compliance, and energy assessments. Expert answers on pricing, timelines, regulations, and services in Exeter & South West UK."
        keywords="EPC questions, SAP FAQ, MEES compliance help, energy certificate questions, EPC regulations UK"
        canonical="/faq"
      />
      <FAQSchema faqs={faqs} />
      <Breadcrumbs items={[
        { name: 'FAQ', path: '/faq' }
      ]} />
      <Navbar />

      {/* Header */}
      <section className="py-16" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 text-white" data-testid="faq-heading">
              Frequently Asked Questions
            </h1>
            <p className="text-base sm:text-lg opacity-95 text-white">
              Find answers to common questions about our services
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {faqs.map((category, catIndex) => (
              <div key={catIndex}>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{category.category}</h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((faq, qIndex) => (
                    <AccordionItem 
                      key={qIndex} 
                      value={`item-${catIndex}-${qIndex}`}
                      className="bg-white border border-gray-200 rounded-lg px-6 shadow-sm hover:shadow-md transition-shadow"
                      data-testid={`faq-item-${catIndex}-${qIndex}`}
                    >
                      <AccordionTrigger className="text-left hover:no-underline py-4">
                        <span className="font-semibold text-gray-900">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-700 pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
            <p className="text-gray-700 mb-8">
              Can't find the answer you're looking for? Our friendly team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact">
                <button className="bg-gradient-to-r from-green-600 to-cyan-500 hover:from-green-700 hover:to-cyan-600 text-white px-8 py-3 rounded-full font-medium" data-testid="faq-contact-btn">
                  Contact Us
                </button>
              </a>
              <a href="tel:01392980077">
                <button className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-3 rounded-full font-medium" data-testid="faq-call-btn">
                  Call 01392 980077
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;